import React from 'react';
import { Link } from 'react-router-dom';
import { ExternalLink, DollarSign, Users, TrendingUp, Award, Zap } from 'lucide-react';
import LumoPromo from '../../components/LumoPromo';

const AffiliatePage = () => {
  // Whop affiliate signup URL
  const WHOP_AFFILIATE_URL = 'https://whop.com/earn-my-edge/affiliates';

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
      {/* Navigation */}
      <nav className="border-b border-slate-800 bg-slate-950/50 backdrop-blur-sm">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <div className="text-2xl font-bold bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
                Earn My Edge
              </div>
            </Link>
            <div className="flex items-center gap-4">
              <Link 
                to="/" 
                className="text-slate-300 hover:text-white transition-colors"
              >
                Home
              </Link>
              <Link 
                to="/authentication" 
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
              >
                Sign In
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <div className="container mx-auto px-6 py-16">
        <div className="text-center max-w-4xl mx-auto mb-16">
          <div className="inline-block px-4 py-2 bg-emerald-600/20 border border-emerald-600/30 rounded-full text-emerald-400 text-sm font-semibold mb-6">
            For Agency Owners & Sales Leaders
          </div>
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
            Stop Spending on Training.<br />
            <span className="bg-gradient-to-r from-emerald-400 to-blue-400 bg-clip-text text-transparent">Start Making Money From It.</span>
          </h1>
          <p className="text-xl text-slate-300 mb-8">
            Your sales team needs constant training. You're spending $10,000+ per year on it. What if you could flip that—get your team world-class AI coaching 24/7 AND earn 20% recurring revenue?
          </p>
          <a
            href={WHOP_AFFILIATE_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-emerald-600 to-blue-600 hover:from-emerald-700 hover:to-blue-700 text-white text-lg font-semibold rounded-xl shadow-lg shadow-emerald-500/20 transition-all"
          >
            See How It Works
            <ExternalLink className="w-5 h-5" />
          </a>
        </div>

        {/* The Problem Section */}
        <div className="bg-gradient-to-br from-red-900/20 to-slate-900/80 backdrop-blur-sm border border-red-800/30 rounded-3xl p-8 md:p-12 mb-20">
          <h2 className="text-3xl md:text-4xl font-bold text-white text-center mb-8">
            The Training Trap Most Sales Leaders Are Stuck In
          </h2>
          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            <div className="text-center">
              <div className="text-5xl font-bold text-red-400 mb-2">$10,000+</div>
              <p className="text-slate-300">Average annual training spend per sales team</p>
            </div>
            <div className="text-center">
              <div className="text-5xl font-bold text-red-400 mb-2">4-12x</div>
              <p className="text-slate-300">Per year you're scheduling training sessions</p>
            </div>
            <div className="text-center">
              <div className="text-5xl font-bold text-red-400 mb-2">0 ROI</div>
              <p className="text-slate-300">Most training doesn't stick. Skills fade in weeks.</p>
            </div>
          </div>
        </div>

        {/* The Flip Section */}
        <div className="max-w-5xl mx-auto mb-20">
          <h2 className="text-3xl md:text-4xl font-bold text-white text-center mb-4">
            Here's The Flip
          </h2>
          <p className="text-xl text-slate-300 text-center mb-12">
            Instead of spending thousands on training, what if you could train your team better AND generate revenue?
          </p>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-gradient-to-br from-emerald-900/20 to-slate-900/80 backdrop-blur-sm border border-emerald-800/30 rounded-2xl p-8">
              <div className="w-12 h-12 bg-emerald-600/20 rounded-xl flex items-center justify-center mb-4">
                <DollarSign className="w-6 h-6 text-emerald-400" />
              </div>
              <h3 className="text-xl font-bold text-white mb-3">Make Money Instead</h3>
              <p className="text-slate-400">
                Earn 20% recurring commissions when your team subscribes. A 10-person team at $99/mo = $198/mo passive income = $2,376/year.
              </p>
            </div>

            <div className="bg-gradient-to-br from-blue-900/20 to-slate-900/80 backdrop-blur-sm border border-blue-800/30 rounded-2xl p-8">
              <div className="w-12 h-12 bg-blue-600/20 rounded-xl flex items-center justify-center mb-4">
                <Users className="w-6 h-6 text-blue-400" />
              </div>
              <h3 className="text-xl font-bold text-white mb-3">24/7 AI Training</h3>
              <p className="text-slate-400">
                No more scheduling sessions or hiring trainers. Your team gets personalized AI coaching anytime, anywhere. LUMO never sleeps.
              </p>
            </div>

            <div className="bg-gradient-to-br from-purple-900/20 to-slate-900/80 backdrop-blur-sm border border-purple-800/30 rounded-2xl p-8">
              <div className="w-12 h-12 bg-purple-600/20 rounded-xl flex items-center justify-center mb-4">
                <Award className="w-6 h-6 text-purple-400" />
              </div>
              <h3 className="text-xl font-bold text-white mb-3">Ultimate Flexibility</h3>
              <p className="text-slate-400">
                You choose: subsidize the cost, split it 50/50, or make your sales reps pay. Either way, you make money and they get better.
              </p>
            </div>
          </div>
        </div>

        {/* Demo/Overview Section */}
        <div className="bg-gradient-to-br from-slate-900/80 to-slate-800/80 backdrop-blur-sm border border-slate-700 rounded-3xl p-8 md:p-12 mb-20">
          <div className="text-center mb-8">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              What Your Team Gets
            </h2>
            <p className="text-lg text-slate-300">
              World-class AI coaching that actually works—proven frameworks, instant feedback, 24/7 availability
            </p>
          </div>

          {/* Video Embed or Screenshot */}
          <div className="aspect-video bg-slate-950 rounded-2xl overflow-hidden mb-8 relative border border-slate-700">
            {/* Replace with actual video embed or use a screenshot */}
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-center">
                <div className="w-20 h-20 bg-emerald-600/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Zap className="w-10 h-10 text-emerald-400" />
                </div>
                <p className="text-slate-400 text-lg">Demo Video Coming Soon</p>
                <p className="text-slate-500 text-sm mt-2">See how Earn My Edge transforms sales training</p>
              </div>
            </div>
            
            {/* Example: YouTube embed structure (uncomment and add your video ID)
            <iframe
              className="w-full h-full"
              src="https://www.youtube.com/embed/YOUR_VIDEO_ID"
              title="Earn My Edge Demo"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
            */}
          </div>

          {/* Key Features Grid */}
          <div className="grid md:grid-cols-2 gap-6">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 bg-emerald-600/20 rounded-lg flex items-center justify-center flex-shrink-0">
                <Users className="w-5 h-5 text-emerald-400" />
              </div>
              <div>
                <h4 className="text-white font-semibold mb-1">AI Sales Coach (LUMO)</h4>
                <p className="text-slate-400 text-sm">
                  Instant feedback on every drill, objection, and roleplay. No scheduling, no waiting—just practice and improve.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="w-10 h-10 bg-blue-600/20 rounded-lg flex items-center justify-center flex-shrink-0">
                <TrendingUp className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <h4 className="text-white font-semibold mb-1">10+ Proven Sales Frameworks</h4>
                <p className="text-slate-400 text-sm">
                  SPIN, MEDDIC, Sandler, Challenger, GAP Selling—all the frameworks top performers use.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="w-10 h-10 bg-purple-600/20 rounded-lg flex items-center justify-center flex-shrink-0">
                <Award className="w-5 h-5 text-purple-400" />
              </div>
              <div>
                <h4 className="text-white font-semibold mb-1">Daily Personalized Drills</h4>
                <p className="text-slate-400 text-sm">
                  Every rep gets drills matched to their skill level and industry. Adaptive learning that actually sticks.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="w-10 h-10 bg-orange-600/20 rounded-lg flex items-center justify-center flex-shrink-0">
                <Zap className="w-5 h-5 text-orange-400" />
              </div>
              <div>
                <h4 className="text-white font-semibold mb-1">Track Team Performance</h4>
                <p className="text-slate-400 text-sm">
                  See who's training, who's improving, and where your team needs help. Real analytics, not guesswork.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Who Should Join */}
        <div className="max-w-4xl mx-auto mb-20">
          <h2 className="text-3xl md:text-4xl font-bold text-white text-center mb-4">
            Who Is This For?
          </h2>
          <p className="text-xl text-slate-300 text-center mb-12">
            Business owners and sales leaders who are tired of expensive, ineffective training
          </p>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-slate-900/50 backdrop-blur-sm border border-slate-800 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-white mb-2">Agency Owners</h3>
              <p className="text-slate-400">
                Manage 5-50 sales reps? Stop spending $15-50k/year on trainers. Get better results, save time, and earn commissions on every rep.
              </p>
            </div>
            <div className="bg-slate-900/50 backdrop-blur-sm border border-slate-800 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-white mb-2">Business Owners with Sales Teams</h3>
              <p className="text-slate-400">
                Whether you have 3 reps or 30, Earn My Edge scales with you. Train 24/7 without the hassle of scheduling or hiring external trainers.
              </p>
            </div>
            <div className="bg-slate-900/50 backdrop-blur-sm border border-slate-800 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-white mb-2">Sales VPs & Directors</h3>
              <p className="text-slate-400">
                Tired of reps forgetting what they learned in quarterly training? Daily AI coaching keeps skills sharp year-round.
              </p>
            </div>
            <div className="bg-slate-900/50 backdrop-blur-sm border border-slate-800 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-white mb-2">Startup Founders</h3>
              <p className="text-slate-400">
                Can't afford a full-time sales trainer? Earn My Edge gives your early sales hires professional coaching at a fraction of the cost.
              </p>
            </div>
          </div>
        </div>

        {/* How It Works */}
        <div className="max-w-4xl mx-auto mb-20">
          <h2 className="text-3xl md:text-4xl font-bold text-white text-center mb-4">
            Three Ways to Structure This (Your Choice)
          </h2>
          <p className="text-xl text-slate-300 text-center mb-8">
            You get your affiliate link. How you use it with your team is 100% up to you.
          </p>
          <div className="bg-slate-900/50 backdrop-blur-sm border border-slate-700 rounded-xl p-6 mb-8">
            <p className="text-slate-300 text-center">
              <span className="font-semibold text-white">Important:</span> These are examples of how you could structure this with your team. 
              We just provide the affiliate link and commissions—you decide your own business model.
            </p>
          </div>
          <div className="space-y-6">
            <div className="flex items-start gap-4 bg-slate-900/50 backdrop-blur-sm border border-slate-800 rounded-xl p-6">
              <div className="w-8 h-8 bg-emerald-600 rounded-full flex items-center justify-center flex-shrink-0 text-white font-bold">
                1
              </div>
              <div>
                <h3 className="text-xl font-semibold text-white mb-2">You Subsidize (Recommended)</h3>
                <p className="text-slate-400 mb-3">
                  Pay for your team's subscriptions as a business expense—still cheaper than traditional training. You earn 20% commissions back automatically. Net cost: $79/rep instead of $99.
                </p>
                <div className="bg-emerald-900/20 border border-emerald-800/30 rounded-lg p-4">
                  <p className="text-emerald-400 font-semibold">Example: 10 reps × $99/mo = $990/mo cost → You earn $198/mo back = $792 net cost</p>
                  <p className="text-slate-400 text-sm mt-2">Compare to: Hiring a trainer at $5k/mo or quarterly training at $3k+ per session</p>
                  <p className="text-slate-300 text-sm mt-2 italic">How you pay for it (company card, reimbursement, etc.) is your call.</p>
                </div>
              </div>
            </div>
            <div className="flex items-start gap-4 bg-slate-900/50 backdrop-blur-sm border border-slate-800 rounded-xl p-6">
              <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0 text-white font-bold">
                2
              </div>
              <div>
                <h3 className="text-xl font-semibold text-white mb-2">Split the Cost 50/50</h3>
                <p className="text-slate-400 mb-3">
                  You cover half, they cover half. You still earn 20% commissions on the full subscription amount. This is just one way to position it—the actual split is between you and your team.
                </p>
                <div className="bg-blue-900/20 border border-blue-800/30 rounded-lg p-4">
                  <p className="text-blue-400 font-semibold">Example: 10 reps × $49.50 cost to you = $495/mo → You earn $198/mo back = $297 net cost</p>
                  <p className="text-slate-400 text-sm mt-2">Your reps invest in their own development, you supplement it, everyone wins.</p>
                  <p className="text-slate-300 text-sm mt-2 italic">You work out the payment arrangement directly with your team.</p>
                </div>
              </div>
            </div>
            <div className="flex items-start gap-4 bg-slate-900/50 backdrop-blur-sm border border-slate-800 rounded-xl p-6">
              <div className="w-8 h-8 bg-purple-600 rounded-full flex items-center justify-center flex-shrink-0 text-white font-bold">
                3
              </div>
              <div>
                <h3 className="text-xl font-semibold text-white mb-2">They Pay, You Profit</h3>
                <p className="text-slate-400 mb-3">
                  Make it a professional development requirement: reps pay for their own subscription using your affiliate link. You earn 20% on every subscription as pure profit. How you position this to your team is up to you.
                </p>
                <div className="bg-purple-900/20 border border-purple-800/30 rounded-lg p-4">
                  <p className="text-purple-400 font-semibold">Example: 10 reps × $99/mo = $0 cost to you → You earn $198/mo passive income = $2,376/year</p>
                  <p className="text-slate-400 text-sm mt-2">Position it as a professional development investment. Serious reps will pay.</p>
                  <p className="text-slate-300 text-sm mt-2 italic">This is purely between you and your team—we just send you commissions.</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="max-w-3xl mx-auto text-center bg-gradient-to-r from-emerald-600/20 to-blue-600/20 backdrop-blur-sm border border-emerald-500/20 rounded-3xl p-12">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Ready to Flip Your Training Model?
          </h2>
          <p className="text-lg text-slate-300 mb-8">
            Join agency owners and sales leaders who are making money from training instead of spending it
          </p>
          <a
            href={WHOP_AFFILIATE_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-emerald-600 to-blue-600 hover:from-emerald-700 hover:to-blue-700 text-white text-lg font-semibold rounded-xl shadow-lg shadow-emerald-500/20 transition-all"
          >
            Get Started Free
            <ExternalLink className="w-5 h-5" />
          </a>
          <p className="text-slate-400 text-sm mt-6">
            No upfront costs. Set up your affiliate link in 5 minutes. Start earning on every subscription.
          </p>
        </div>

        {/* Lumo Promo teaser (static) */}
        <div className="mt-20">
          <LumoPromo />
        </div>
      </div>

      {/* Footer */}
      <footer className="border-t border-slate-800 mt-20">
        <div className="container mx-auto px-6 py-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="text-slate-400 text-sm">
              © 2026 Earn My Edge. All rights reserved.
            </div>
            <div className="flex items-center gap-6">
              <Link to="/terms" className="text-slate-400 hover:text-white text-sm transition-colors">
                Terms
              </Link>
              <Link to="/privacy" className="text-slate-400 hover:text-white text-sm transition-colors">
                Privacy
              </Link>
              <Link to="/authentication" className="text-slate-400 hover:text-white text-sm transition-colors">
                Sign In
              </Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default AffiliatePage;
